const HTML_ESCAPES = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
};

export function escapeHtml(value) {
    if (value == null) {
        return "";
    }
    return String(value).replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}

/**
 * Answer text is submitted by anonymous respondents and the result is rendered
 * with v-html to keep line breaks, so it must be escaped before the <br> are
 * introduced.
 */
export function formatAnswerText(text) {
    return escapeHtml(text).replace(/(?:\r\n|\r|\n)/g, "<br>");
}

/**
 * Only http(s) urls may reach an href. A stored "javascript:" value would
 * otherwise run on click, and answer file urls come from the respondent.
 */
export function safeFileUrl(url) {
    if (typeof url !== "string") {
        return "";
    }
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
        return "";
    }
    return trimmed;
}

/**
 * Filenames are derived from urls that may contain malformed percent escapes,
 * which make decodeURIComponent throw during render.
 */
export function fileNameFromUrl(url) {
    // Links are presigned, so the signature has to come off before the last
    // path segment is treated as a filename.
    const segment = String(url || "")
        .split(/[?#]/)[0]
        .split("/")
        .pop();
    try {
        return decodeURIComponent(segment);
    } catch (e) {
        return segment;
    }
}

// Formats every browser can draw in an <img>. HEIC/TIFF/DST/PDF keep the
// file chip.
const PREVIEW_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "avif", "ico"];

export function fileExtension(file) {
    const source = (file && (file.name || fileNameFromUrl(file.url))) || "";
    const parts = String(source).split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

/**
 * Whether an answer file can be shown as a thumbnail. Uses the file name
 * (or the url path, without the signature) and never the mime type alone,
 * since image/heic is not drawable in most browsers.
 */
export function isPreviewableImage(file) {
    return PREVIEW_EXTENSIONS.includes(fileExtension(file));
}

export function formatSelectboxValue(answer) {
    try {
        return JSON.parse(answer.valueText).join(",");
    } catch (e) {
        return answer.valueText;
    }
}

export function getEmotionIcon(value) {
    const score = Math.round(parseFloat(value) || 0);
    if (score >= 5) {
        return "mdi mdi-emoticon-excited-outline answer-type-icon";
    }
    if (score >= 4) {
        return "mdi mdi-emoticon-happy-outline answer-type-icon";
    }
    if (score >= 3) {
        return "mdi mdi-emoticon-neutral-outline answer-type-icon";
    }
    if (score >= 2) {
        return "mdi mdi-emoticon-sad-outline answer-type-icon";
    }
    return "mdi mdi-emoticon-cry-outline answer-type-icon";
}

export function getIconForAnswer(answer) {
    if (!answer) {
        return "mdi mdi-circle-small answer-type-icon";
    }
    if (answer.type === "emotion") {
        return getEmotionIcon(answer.valueNumber);
    }
    const icons = {
        comment: "mdi mdi-comment-text-outline answer-type-icon",
        boolean: "mdi mdi-check-circle-outline answer-type-icon",
        selectbox: "mdi mdi-format-list-bulleted answer-type-icon",
        input: "mdi mdi-format-text answer-type-icon",
        nps: "mdi mdi-gauge answer-type-icon",
        ces: "mdi mdi-arm-flex-outline answer-type-icon",
        date: "mdi mdi-calendar-outline answer-type-icon",
        file: "mdi mdi-paperclip answer-type-icon",
        table: "mdi mdi-table answer-type-icon",
    };
    return icons[answer.type] || "mdi mdi-circle-small answer-type-icon";
}

export function answerTone(answer) {
    if (
        !answer ||
        answer.valueNumber == null ||
        answer.valueNumber === "" ||
        ["comment", "input", "selectbox", "date", "file", "table"].includes(answer.type)
    ) {
        return "muted";
    }
    const value = parseFloat(answer.valueNumber);
    if (answer.type === "nps") {
        if (value > 8) return "good";
        if (value > 6) return "neutral";
        return "bad";
    }
    if (answer.type === "ces") {
        if (value > 4) return "good";
        if (value === 4) return "neutral";
        return "bad";
    }
    if (answer.type === "emotion") {
        if (value >= 4) return "good";
        if (value >= 3) return "neutral";
        return "bad";
    }
    if (answer.type === "boolean") {
        return value > 0 ? "good" : "bad";
    }
    return "muted";
}

export function answerToneClass(answer) {
    const tone = answerTone(answer);
    return tone ? `tone-${tone}` : "tone-muted";
}

export function npsBandKey(valueNumber) {
    if (valueNumber > 8) {
        return "inbox-message-nps_promoter";
    }
    if (valueNumber > 6) {
        return "inbox-message-nps_neutral";
    }
    return "inbox-message-nps_detractor";
}
